import React, { useState } from 'react'
import { Button, Input, Select } from './ui'
import { CALL_OUTCOMES, OUTCOME_FIELD_LABELS, getOutcome } from '../lib/workflow'
import { AGENTS, CALL_PURPOSES } from '../lib/constants'

// Outcome-driven call logger. Pick an outcome, fill only the relevant fields, save.
// Props:
//   lead   = { agent, prospect_name, company } prefill (optional)
//   onSave = async ({ outcomeId, form, lead }) => void   (throws on error)
//   onClose = () => void
export default function LogCall({ lead = {}, onSave, onClose }) {
  const [outcomeId, setOutcomeId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [head, setHead] = useState({
    agent: lead.agent || AGENTS[0],
    prospect_name: lead.prospect_name || '',
    company: lead.company || '',
    call_purpose: lead.call_purpose || '',
    // lead origin: 'Event' (needs event_name) or 'Organic' (self-sourced)
    lead_origin: lead.event_name ? 'Event' : (lead.source === 'Event' ? 'Event' : 'Organic'),
    event_name: lead.event_name || '',
  })
  const [form, setForm] = useState({})
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const setH = (k, v) => setHead(h => ({ ...h, [k]: v }))

  const o = outcomeId ? getOutcome(outcomeId) : null

  const renderField = (fieldId) => {
    const label = OUTCOME_FIELD_LABELS[fieldId] || fieldId
    switch (fieldId) {
      case 'callback_at':
      case 'meeting_at':
        return <Input key={fieldId} label={label} type="datetime-local" value={form[fieldId] || ''} onChange={e => setF(fieldId, e.target.value)} />
      case 'next_action_at':
        return <Input key={fieldId} label={label} type="date" value={form[fieldId] || ''} onChange={e => setF(fieldId, e.target.value)} />
      case 'bant_score':
        return (
          <Select key={fieldId} label={label} value={form[fieldId] ?? ''} onChange={e => setF(fieldId, e.target.value)}>
            <option value="">—</option>
            {[0, 1, 2, 3, 4].map(n => <option key={n} value={n}>{n}{n === 4 ? ' — Fully Qualified' : n === 3 ? ' — Qualified' : ''}</option>)}
          </Select>
        )
      case 'service_interest':
        return (
          <Select key={fieldId} label={label} value={form[fieldId] || ''} onChange={e => setF(fieldId, e.target.value)}>
            <option value="">—</option>
            {['Cloud Infrastructure', 'Data & Analytics', 'APM & Monitoring', 'Cybersecurity', 'ERP (Odoo)', 'GitLab', 'Lainnya'].map(s => <option key={s}>{s}</option>)}
          </Select>
        )
      case 'invalid_field':
        return (
          <Select key={fieldId} label={label} value={form[fieldId] || ''} onChange={e => setF(fieldId, e.target.value)}>
            <option value="">—</option>
            {['Nomor telepon', 'Email', 'Keduanya'].map(s => <option key={s}>{s}</option>)}
          </Select>
        )
      default:
        return <Input key={fieldId} label={label} value={form[fieldId] || ''} onChange={e => setF(fieldId, e.target.value)} style={{ flex: 2 }} />
    }
  }

  const handleSave = async () => {
    if (!head.prospect_name || !head.company) { setErr('Prospect & company wajib diisi.'); return }
    if (!head.call_purpose) { setErr('Pilih tujuan call dulu.'); return }
    if (head.lead_origin === 'Event' && !head.event_name.trim()) { setErr('Isi nama event asal lead.'); return }
    if (!outcomeId) { setErr('Pilih outcome call dulu.'); return }
    // required conditional fields
    if (outcomeId === 'call_later' && !form.callback_at) { setErr('Isi waktu callback.'); return }
    if (outcomeId === 'meeting' && !form.meeting_at) { setErr('Isi tanggal & waktu meeting.'); return }
    if (outcomeId === 'interested' && !form.next_action_at) { setErr('Isi tanggal next action.'); return }
    if (outcomeId === 'not_interested' && !form.reason) { setErr('Isi alasan.'); return }
    if (outcomeId === 'dnc' && !form.reason) { setErr('Isi alasan Do Not Contact.'); return }

    setSaving(true); setErr('')
    try {
      await onSave({ outcomeId, form, lead: { ...head } })
      onClose()
    } catch (e) {
      setErr('Error: ' + e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 560, maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Log Call</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>×</button>
        </div>

        {/* Who */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          <Select label="Agent" value={head.agent} onChange={e => setH('agent', e.target.value)}>
            {AGENTS.map(a => <option key={a}>{a}</option>)}
          </Select>
          <Input label="Prospect" value={head.prospect_name} onChange={e => setH('prospect_name', e.target.value)} />
          <Input label="Company" value={head.company} onChange={e => setH('company', e.target.value)} />
        </div>

        {/* Tagging: why this call + where the lead came from */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          <Select label="Tujuan call *" value={head.call_purpose} onChange={e => setH('call_purpose', e.target.value)}>
            <option value="">— pilih —</option>
            {CALL_PURPOSES.map(p => <option key={p}>{p}</option>)}
          </Select>
          <Select label="Asal lead" value={head.lead_origin} onChange={e => setH('lead_origin', e.target.value)}>
            <option value="Organic">Cari sendiri (organic)</option>
            <option value="Event">Dari event</option>
          </Select>
          {head.lead_origin === 'Event' && (
            <Input label="Nama event *" value={head.event_name} placeholder="Nama event..." onChange={e => setH('event_name', e.target.value)} />
          )}
        </div>

        {/* Outcome picker */}
        <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 8 }}>Hasil call</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
          {CALL_OUTCOMES.map(oc => {
            const active = outcomeId === oc.id
            return (
              <button key={oc.id} onClick={() => { setOutcomeId(oc.id); setErr('') }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', cursor: 'pointer',
                  padding: '10px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  border: active ? '2px solid #185FA5' : '0.5px solid rgba(0,0,0,0.15)',
                  background: active ? '#EAF2FB' : '#fff', color: active ? '#185FA5' : '#333',
                }}>
                <span style={{ fontSize: 16 }}>{oc.icon}</span> {oc.label}
              </button>
            )
          })}
        </div>

        {/* Conditional fields */}
        {o && (
          <div style={{ background: '#f9f9f7', borderRadius: 10, padding: 12, marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>
              {o.task ? `Akan otomatis membuat task: ${o.task.title}.` : 'Tidak ada follow-up otomatis.'}
              {o.cancelOutreach ? ' Outreach task yang menunggu akan dibatalkan.' : ''}
              {o.suppress ? ' Lead akan ditandai Do Not Contact.' : ''}
            </div>
            {o.fields.length === 0 ? (
              <div style={{ fontSize: 12, color: '#777' }}>Tidak perlu input tambahan. Klik simpan.</div>
            ) : (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {o.fields.map(renderField)}
              </div>
            )}
            <div style={{ marginTop: 8 }}>
              <Input label="Catatan (opsional)" value={form.notes || ''} onChange={e => setF('notes', e.target.value)} style={{ flex: 3 }} />
            </div>
          </div>
        )}

        {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', borderRadius: 8, padding: '9px 12px', fontSize: 12, marginBottom: 12 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>Batal</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || !outcomeId}>
            {saving ? 'Menyimpan...' : 'Simpan Call'}
          </Button>
        </div>
      </div>
    </div>
  )
}
